const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface LoginRequest {
  companyNo: string;
  operatorId: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  companyNo: string;
  customerNo: string;
  operatorId: string;
  operatorName: string;
  operatorGroupId: string;
  expiresAt: string;
}

export interface UserContext {
  companyNo: string;
  customerNo: string;
  operatorId: string;
  operatorName: string;
  operatorGroupId: string;
}

export class AuthService {
  static async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    return await response.json();
  }

  static async logout(): Promise<void> {
    const token = this.getToken();
    if (token) {
      try {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }
    this.removeToken();
  }

  static setToken(token: string): void {
    localStorage.setItem('bizclick_token', token);
  }

  static getToken(): string | null {
    return localStorage.getItem('bizclick_token');
  }

  static removeToken(): void {
    localStorage.removeItem('bizclick_token');
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  static getUserDataFromToken(): UserContext | null {
    const token = this.getToken();
    if (!token) return null;

    try {
        //const payload = JSON.parse(atob(token.split('.')[1]));

        const payload = JSON.parse(
            decodeURIComponent(
                atob(token.split('.')[1])
                    .split('')
                    .map(char => `%${('00' + char.charCodeAt(0).toString(16)).slice(-2)}`)
                    .join('')
            )
        );


      return {
        companyNo: payload.CompanyNo,
        customerNo: payload.CustomerNo,
        operatorId: payload.OperatorId,
        operatorName: payload.OperatorName,
        operatorGroupId: payload.OperatorGroupId
      };




    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }

  static async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();

    if (!token) {
      throw new Error('No authentication token available');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 401) {
      this.removeToken();
      window.location.reload();
    }

    return response;
  }

  static async authenticatedGet(url: string, options?: RequestInit): Promise<Response> {
    return this.authenticatedFetch(url, { method: 'GET', ...options });
  }

  static async authenticatedPost(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedPut(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedPatch(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  static async authenticatedDelete(url: string, data?: any): Promise<Response> {
    return this.authenticatedFetch(url, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined
    });
  }
}
